import React, { useMemo, useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Download, ClipboardCheck, ChevronDown, Check, BarChart3, Ticket, Gift, Printer, QrCode, Megaphone, Share2, Mail, Users, ShieldCheck, Sparkles } from 'lucide-react';
interface SupportChecklistModalProps {
  isOpen: boolean;
  onClose: () => void;
}
interface SupportSection {
  id: string;
  title: string;
  icon: ComponentType<{
    size?: number;
    className?: string;
  }>;
  items: string[];
  boundary?: string;
}
const SECTIONS: SupportSection[] = [{
  id: 'reporting',
  title: 'Daily Campaign Reporting & Recommendations',
  icon: BarChart3,
  items: ['Number of Snak™ completions per day', 'Daily entry list for raffle purposes', 'Drop-off or completion issues that may need attention', 'Short daily performance summary', 'Overall post-event summary']
}, {
  id: 'raffle-entry',
  title: 'Raffle Entry Support',
  icon: Ticket,
  items: ['Provide daily list of people who completed the Snak™', 'Receive list of booth visitors who get double entries', 'Combine standard + double entries into one draw list', 'Run combined list through a free online name picker', 'Record the name picker draw for auditing if required', 'Share the selected winner name/details with Minerva'],
  boundary: 'Minerva remains responsible for announcing the winner, contacting the winner, and handing over or sending the prize.'
}, {
  id: 'prize',
  title: 'Raffle Prize Assistance',
  icon: Gift,
  items: ['Suggested prize options', 'Links to places where Minerva can purchase prizes', 'Reminder timeline for when prizes need to be ordered', 'Follow-up reminders to confirm prizes have been purchased', 'Delivery / fulfilment checklist', 'Reminder to confirm prizes have arrived before the event', 'Fallback suggestions if prizes are delayed'],
  boundary: 'Snakker does not purchase prizes directly.'
}, {
  id: 'print',
  title: 'Print Media Design & Print Coordination',
  icon: Printer,
  items: ['QR flyers', 'Business cards', 'Table tent cards', 'Booth banner', 'Badge / lanyard cards', 'Booster cheat sheet for reps', 'Prepare files for print', 'Advise on print specifications', 'Coordinate with a printer after Minerva approval', 'Check QR code scannability before printing', 'Follow up on print production and delivery timelines', "Coordinate delivery of printed materials to Minerva's address", 'Confirm with Minerva that printed materials have arrived'],
  boundary: 'Minerva approves all materials, confirms delivery details, and covers print, courier or delivery costs.'
}, {
  id: 'qr',
  title: 'QR Code and Link Support',
  icon: QrCode,
  items: ['Create QR codes for different placements', 'Create different tracking links for print, booth, email and social', 'Supply QR files for print materials', 'Test QR codes before final use', 'Provide fallback short links where relevant']
}, {
  id: 'winner-comms',
  title: 'Daily Winner Communication Writeups',
  icon: Megaphone,
  items: ['Winner announcement writeups for social media', 'Direct email writeups for Minerva to send to winners', 'Short internal message templates for Minerva reps/team', 'Suggested winner consent wording if names/photos are used publicly', 'Suggested reminder copy to confirm prize fulfilment after the event'],
  boundary: 'Minerva is responsible for sending all winner emails, posting on social, contacting winners and managing prize fulfilment.'
}, {
  id: 'social',
  title: 'Social Media Writeup Support',
  icon: Share2,
  items: ['Pre-event announcement posts', '"Visit us at the booth" posts', 'Daily winner posts', 'Daily campaign insight posts', 'Post-event thank-you posts', 'Post-event insight / benchmark posts'],
  boundary: 'Minerva approves and posts through its own channels.'
}, {
  id: 'email',
  title: 'Email Writeup Support',
  icon: Mail,
  items: ['Pre-event invitation email', 'Pre-event reminder email', '"Visit us at the booth" email', 'Snak™ participation / confirmation email copy', 'Daily or during-event reminder email copy', 'Winner notification email copy', 'Post-event thank-you email', 'Post-event peer benchmark / insight email', 'Follow-up email for priority leads'],
  boundary: "Snakker has no access to Minerva's CRM or marketing automation. Minerva approves, sets up, sends and manages all email through its own systems."
}, {
  id: 'training',
  title: 'Rep Training Support',
  icon: Users,
  items: ['How to introduce the Snak™ at the booth', 'How to use the Snak™ as an icebreaker', 'How to collect booth-visit double entries', 'How to explain the raffle', 'How to avoid influencing answers', 'How to reset / refresh between sessions', 'What to do if Wi-Fi, QR codes or devices create issues']
}, {
  id: 'risk',
  title: 'Risk Mitigation & Go / No-Go Support',
  icon: ShieldCheck,
  items: ['Snak™ access readiness', 'QR code testing', 'Print material readiness', 'Device readiness', 'Raffle process readiness', 'Prize delivery confirmation', 'Rep readiness', 'Fallback process if tech fails']
}];
interface Tier {
  id: 'light' | 'managed' | 'extended';
  name: string;
  tagline: string;
  bullets: string[];
  accent: string;
  ring: string;
}
const TIERS: Tier[] = [{
  id: 'light',
  name: 'Light Support',
  tagline: 'The essentials, hands-off.',
  accent: 'from-slate-50 to-slate-100',
  ring: 'border-slate-300',
  bullets: ['Technical support on congress days', 'Reporting & daily summaries', 'QR files & raffle instructions', 'Prize links & rep training', 'Email + social write-up templates']
}, {
  id: 'managed',
  name: 'Managed Support',
  tagline: 'Light + active raffle operations.',
  accent: 'from-cyan-50 to-blue-50',
  ring: 'border-minerva-blue',
  bullets: ['Everything in Light', 'Daily entry list support', 'Double-entry processing', 'Name-picker draw support', 'Print-ready files']
}, {
  id: 'extended',
  name: 'Extended Support',
  tagline: 'Managed + full coordination.',
  accent: 'from-amber-50 to-orange-50',
  ring: 'border-amber-500',
  bullets: ['Everything in Managed', 'Print coordination & delivery', 'Prize logistics reminders', 'Daily winner communication writeups', 'Post-event reporting & follow-up content']
}];
export function SupportChecklistModal({
  isOpen,
  onClose
}: SupportChecklistModalProps) {
  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const [openSection, setOpenSection] = useState<string | null>(SECTIONS[0].id);
  const [selectedTier, setSelectedTier] = useState<Tier['id'] | null>(null);
  const toggleItem = (key: string) => {
    setChecked(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };
  const toggleSection = (sectionId: string, items: string[]) => {
    const allChecked = items.every((_, i) => checked[`${sectionId}-${i}`]);
    const newState = {
      ...checked
    };
    items.forEach((_, i) => {
      newState[`${sectionId}-${i}`] = !allChecked;
    });
    setChecked(newState);
  };
  const totalSelected = useMemo(() => Object.values(checked).filter(Boolean).length, [checked]);
  const totalItems = useMemo(() => SECTIONS.reduce((sum, s) => sum + s.items.length, 0), []);
  const handlePrint = () => {
    window.print();
  };
  return <AnimatePresence data-id="element-272">
      {isOpen && <>
          {/* Print styles */}
          <style data-id="element-273">{`
            @media print {
              @page { size: A4; margin: 14mm; }
              body * { visibility: hidden !important; }
              #support-checklist-print, #support-checklist-print * {
                visibility: visible !important;
              }
              #support-checklist-print {
                position: absolute !important;
                inset: 0 !important;
                width: 100% !important;
                background: white !important;
                padding: 0 !important;
                overflow: visible !important;
              }
              .no-print { display: none !important; }
              .print-show { display: block !important; }
              .print-section {
                page-break-inside: avoid !important;
                break-inside: avoid !important;
              }
              .print-open { display: block !important; max-height: none !important; }
            }
          `}</style>

          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="absolute inset-0 z-[60] flex flex-col bg-slate-900/60 backdrop-blur-md no-print" data-id="element-274">
            <motion.div initial={{
          y: '100%'
        }} animate={{
          y: 0
        }} exit={{
          y: '100%'
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 200
        }} className="mt-auto h-[94%] bg-white rounded-t-3xl shadow-2xl flex flex-col overflow-hidden" data-id="element-275">
              {/* Header */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 bg-white z-10 shrink-0 no-print" data-id="element-276">
                <div className="flex items-center gap-2" data-id="element-277">
                  <div className="w-8 h-8 rounded-full bg-minerva-blue/10 flex items-center justify-center" data-id="element-278">
                    <ClipboardCheck size={16} className="text-minerva-blue" data-id="element-279" />
                  </div>
                  <div data-id="element-280">
                    <h3 className="text-sm font-bold text-slate-900" data-id="element-281">
                      Where Can Snakker Help?
                    </h3>
                    <p className="text-[11px] font-medium text-slate-500" data-id="element-282">
                      Pick what you'd like our support on
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2" data-id="element-283">
                  <div className="hidden sm:flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-slate-100 text-slate-700 text-[11px] font-bold" data-id="element-284">
                    <Check size={12} className="text-minerva-blue" data-id="element-285" />
                    {totalSelected} / {totalItems}
                  </div>
                  <button onClick={handlePrint} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-minerva-blue text-white text-[11px] font-bold shadow-sm hover:bg-minerva-blue/90 transition-colors" data-id="element-286">
                    <Download size={13} data-id="element-287" />
                    Print / Save PDF
                  </button>
                  <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full bg-slate-100 text-slate-500 hover:bg-slate-200 transition-colors" aria-label="Close" data-id="element-288">
                    <X size={18} data-id="element-289" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto bg-slate-50" data-id="element-290">
                <div id="support-checklist-print" className="p-6 pb-12" data-id="element-291">
                  <div className="max-w-4xl mx-auto" data-id="element-292">
                    {/* Print title */}
                    <div className="hidden print:block border-b border-slate-300 pb-3 mb-5" data-id="element-293">
                      <div className="text-[10px] font-bold tracking-[0.3em] uppercase text-minerva-blue" data-id="element-294">
                        Minerva Imaging · SNMMI 2026
                      </div>
                      <h1 className="text-xl font-display font-bold text-slate-900" data-id="element-295">
                        Areas Where Snakker Can Support Minerva
                      </h1>
                      <p className="text-[11px] text-slate-600 mt-1" data-id="element-296">
                        Selection checklist · {totalSelected} of {totalItems}{' '}
                        items selected
                      </p>
                    </div>

                    {/* Intro framing */}
                    <div className="bg-white border border-slate-200 rounded-2xl p-5 mb-6 shadow-sm" data-id="element-297">
                      <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-100 text-amber-700 text-[10px] font-bold tracking-widest uppercase mb-3" data-id="element-298">
                        <Sparkles size={11} data-id="element-299" />
                        Our concern, your time
                      </div>
                      <p className="text-sm text-slate-700 leading-relaxed mb-2" data-id="element-300">
                        Our job with this isn't the execution. Nevertheless,
                        we're worried that Minerva won't have the time to handle
                        the execution properly.
                      </p>
                      <p className="text-sm text-slate-700 leading-relaxed" data-id="element-301">
                        Below are the areas where we believe support is needed.
                        Tick what you'd like Snakker to help with — or pick a
                        condensed package below.
                      </p>
                    </div>

                    {/* Tier picker */}
                    <div className="mb-6" data-id="element-302">
                      <h2 className="text-[11px] font-bold tracking-[0.25em] uppercase text-slate-500 mb-3" data-id="element-303">
                        Or choose a condensed package
                      </h2>
                      <div className="grid sm:grid-cols-3 gap-3" data-id="element-304">
                        {TIERS.map(tier => <button key={tier.id} onClick={() => setSelectedTier(selectedTier === tier.id ? null : tier.id)} className={`text-left p-4 rounded-2xl border-2 transition-all bg-gradient-to-br ${tier.accent} ${selectedTier === tier.id ? `${tier.ring} shadow-md scale-[1.02]` : 'border-slate-200 hover:border-slate-300'}`} data-id="element-305">
                            <div className="flex items-start justify-between mb-1" data-id="element-306">
                              <div className="text-[13px] font-bold text-slate-900" data-id="element-307">
                                {tier.name}
                              </div>
                              {selectedTier === tier.id && <div className="w-4 h-4 rounded-full bg-minerva-blue flex items-center justify-center shrink-0" data-id="element-308">
                                  <Check size={10} className="text-white" data-id="element-309" />
                                </div>}
                            </div>
                            <div className="text-[11px] text-slate-600 mb-2.5" data-id="element-310">
                              {tier.tagline}
                            </div>
                            <ul className="space-y-1" data-id="element-311">
                              {tier.bullets.map(b => <li key={b} className="text-[11px] text-slate-700 flex items-start gap-1.5" data-id="element-312">
                                  <Check size={10} className="text-minerva-blue mt-0.5 shrink-0" data-id="element-313" />
                                  <span data-id="element-314">{b}</span>
                                </li>)}
                            </ul>
                          </button>)}
                      </div>
                    </div>

                    {/* Detailed sections */}
                    <h2 className="text-[11px] font-bold tracking-[0.25em] uppercase text-slate-500 mb-3" data-id="element-315">
                      Or pick item by item
                    </h2>
                    <div className="space-y-3" data-id="element-316">
                      {SECTIONS.map((section, sIdx) => {
                    const sectionItemKeys = section.items.map((_, i) => `${section.id}-${i}`);
                    const sectionSelected = sectionItemKeys.filter(k => checked[k]).length;
                    const allChecked = sectionSelected === section.items.length;
                    const someChecked = sectionSelected > 0 && !allChecked;
                    const isOpen = openSection === section.id;
                    const Icon = section.icon;
                    return <div key={section.id} className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm print-section" data-id="element-317">
                            {/* Section header */}
                            <div className="flex items-center gap-2 px-4 py-3" data-id="element-318">
                              <button onClick={e => {
                          e.stopPropagation();
                          toggleSection(section.id, section.items);
                        }} className={`shrink-0 w-5 h-5 rounded-md border-2 flex items-center justify-center transition-colors ${allChecked ? 'bg-minerva-blue border-minerva-blue' : someChecked ? 'bg-minerva-blue/40 border-minerva-blue' : 'bg-white border-slate-300 hover:border-slate-400'}`} aria-label="Toggle all in section" data-id="element-319">
                                {(allChecked || someChecked) && <Check size={12} className="text-white" strokeWidth={3} data-id="element-320" />}
                              </button>

                              <button onClick={() => setOpenSection(isOpen ? null : section.id)} className="flex-1 flex items-center gap-2.5 text-left" data-id="element-321">
                                <div className="w-8 h-8 rounded-full bg-minerva-blue/10 flex items-center justify-center shrink-0" data-id="element-322">
                                  <Icon size={15} className="text-minerva-blue" data-id="element-323" />
                                </div>
                                <div className="flex-1 min-w-0" data-id="element-324">
                                  <div className="flex items-center gap-2" data-id="element-325">
                                    <span className="text-[10px] font-bold text-slate-400" data-id="element-326">
                                      {String(sIdx + 1).padStart(2, '0')}
                                    </span>
                                    <h3 className="text-[13px] font-bold text-slate-900 leading-tight" data-id="element-327">
                                      {section.title}
                                    </h3>
                                  </div>
                                  <div className="text-[10px] text-slate-500 mt-0.5" data-id="element-328">
                                    {sectionSelected} of {section.items.length}{' '}
                                    selected
                                  </div>
                                </div>
                                <ChevronDown size={16} className={`text-slate-400 shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} data-id="element-329" />
                              </button>
                            </div>

                            {/* Section items */}
                            <AnimatePresence initial={false} data-id="element-330">
                              {isOpen && <motion.div initial={{
                          height: 0,
                          opacity: 0
                        }} animate={{
                          height: 'auto',
                          opacity: 1
                        }} exit={{
                          height: 0,
                          opacity: 0
                        }} transition={{
                          duration: 0.25
                        }} className="overflow-hidden print-open" data-id="element-331">
                                  <div className="px-4 pb-4 pt-1 border-t border-slate-100 bg-slate-50/50" data-id="element-332">
                                    <ul className="space-y-1.5 mt-2" data-id="element-333">
                                      {section.items.map((item, i) => {
                                const key = `${section.id}-${i}`;
                                const isChecked = !!checked[key];
                                return <li key={key} data-id="element-334">
                                            <label className="flex items-start gap-2.5 cursor-pointer group py-1" data-id="element-335">
                                              <span className={`shrink-0 mt-0.5 w-4 h-4 rounded border-2 flex items-center justify-center transition-colors ${isChecked ? 'bg-minerva-blue border-minerva-blue' : 'bg-white border-slate-300 group-hover:border-slate-400'}`} data-id="element-336">
                                                {isChecked && <Check size={10} className="text-white" strokeWidth={3} data-id="element-337" />}
                                              </span>
                                              <input type="checkbox" checked={isChecked} onChange={() => toggleItem(key)} className="sr-only" data-id="element-338" />
                                              <span className={`text-[12px] leading-snug ${isChecked ? 'text-slate-500 line-through' : 'text-slate-700'}`} data-id="element-339">
                                                {item}
                                              </span>
                                            </label>
                                          </li>;
                              })}
                                    </ul>

                                    {section.boundary && <div className="mt-3 pl-2 border-l-2 border-amber-300 bg-amber-50/60 rounded-r-md py-2 px-3 text-[11px] text-amber-900" data-id="element-340">
                                        <span className="font-bold uppercase tracking-wider text-[9px] block mb-0.5" data-id="element-341">
                                          Boundary
                                        </span>
                                        {section.boundary}
                                      </div>}
                                  </div>
                                </motion.div>}
                            </AnimatePresence>
                          </div>;
                  })}
                    </div>

                    {/* Footer */}
                    <div className="mt-8 pt-6 border-t border-slate-200 text-center" data-id="element-342">
                      <p className="text-[11px] text-slate-500 mb-2" data-id="element-343">
                        Snakker supports Minerva. Minerva owns the execution,
                        CRM, channels and prize fulfilment.
                      </p>
                      <p className="text-[9px] tracking-widest uppercase text-slate-400" data-id="element-344">
                        Confidential · SNMMI 2026 Support Scope
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </>}
    </AnimatePresence>;
}